import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Questionnaire, Question, Page, AnswerMap, Answer } from "./types";
import {
  buildPages,
  getVisiblePages,
} from "../../helpers/questionnaire-helpers";
import {
  fetchQuestionnaire,
  submitAuthenticated,
  submitUnauthenticated,
} from "../../services/questionnaire-api";
import { buildResponsePayload } from "../../helpers/questionnaire-helpers";
import { useTransifexLoader } from "../transifex-loader";
import { Language } from "../preferred-language";
import { jwtDecode, JwtPayload } from "jwt-decode";

interface PortalJwtPayload extends JwtPayload {
  email?: string;
  app_metadata?: {
    ciq_id?: string;
    location_id?: string;
  };
}

const STORAGE_PREFIX = "pp-survey.";

function storageGet(key: string): string | null {
  return localStorage.getItem(STORAGE_PREFIX + key);
}

function storageSet(key: string, value: string) {
  localStorage.setItem(STORAGE_PREFIX + key, value);
}

function storageClear() {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(STORAGE_PREFIX)) keysToRemove.push(k);
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}

interface QuestionnaireContextType {
  questionnaire: Questionnaire | null;
  questions: Question[];
  pages: Page[];
  visiblePages: Page[];
  currentPageIndex: number;
  answers: AnswerMap;
  isLoading: boolean;
  error: string | null;
  isSubmitting: boolean;
  isComplete: boolean;
  isAuthenticated: boolean;
  progress: number;
  locale: string;
  editingFromSummary: boolean;
  scrollToQuestion: string | null;
  setAnswer: (questionName: string, answer: Answer) => void;
  goToPage: (index: number) => void;
  nextPage: (answersOverride?: AnswerMap) => void;
  prevPage: () => void;
  submitQuestionnaire: () => Promise<void>;
  changeLocale: (newLocale: string) => void;
  restartQuestionnaire: () => void;
  setEditingFromSummary: (editing: boolean) => void;
  setScrollToQuestion: (name: string | null) => void;
}

const QuestionnaireContext = createContext<
  QuestionnaireContextType | undefined
>(undefined);

interface Props {
  children: ReactNode;
}

export function QuestionnaireProvider({ children }: Props) {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const { setLang } = useTransifexLoader();

  const [locale, setLocale] = useState(
    () => searchParams.get("locale") || "en"
  );
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(
    null
  );
  const [questions, setQuestions] = useState<Question[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(-1);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [editingFromSummary, setEditingFromSummary] = useState(false);
  const [scrollToQuestion, setScrollToQuestion] = useState<string | null>(null);

  const isAuthenticated = useMemo(
    () => !token && !!localStorage.getItem("id_token"),
    [token]
  );

  useEffect(() => {
    if (!token) {
      setError("no_token");
      setIsLoading(false);
      return;
    }

    const savedAnswers = storageGet("answers");
    const savedPage = storageGet("currentPage");
    const savedToken = storageGet("token");

    let restored = false;
    if (savedToken === token && savedAnswers) {
      try {
        const parsed = JSON.parse(savedAnswers);
        setAnswers(parsed);
        if (savedPage) setCurrentPageIndex(parseInt(savedPage, 10) || 0);
        restored = true;
      } catch {
        // corrupted storage, ignore
      }
    }

    (async () => {
      try {
        const data = await fetchQuestionnaire(token, locale);
        setQuestionnaire(data);

        const allQuestions = data.survey_modules.flatMap(
          (mod) => mod.questions
        );
        setQuestions(allQuestions);

        const builtPages = buildPages(data, allQuestions);
        setPages(builtPages);

        storageSet("token", token);
        if (restored || isAuthenticated) {
          if (!restored) setCurrentPageIndex(0);
        }
        if (!restored) {
          storageSet("inProgress", "true");
        }
      } catch (err: any) {
        console.error("Failed to fetch questionnaire:", err);
        setError("fetch_failed");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [token, locale]);

  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      storageSet("answers", JSON.stringify(answers));
    }
  }, [answers]);

  useEffect(() => {
    storageSet("currentPage", String(currentPageIndex));
  }, [currentPageIndex]);

  const visiblePages = useMemo(
    () => getVisiblePages(pages, questions, answers),
    [pages, questions, answers]
  );

  const pageIdentityRef = useRef<number>(-1);

  useEffect(() => {
    if (currentPageIndex >= 0 && currentPageIndex < visiblePages.length) {
      pageIdentityRef.current = visiblePages[currentPageIndex].startIndex;
    }
  }, [currentPageIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevVisiblePagesRef = useRef<Page[]>([]);

  useEffect(() => {
    const prev = prevVisiblePagesRef.current;
    prevVisiblePagesRef.current = visiblePages;

    if (pageIdentityRef.current < 0 || currentPageIndex < 0) return;
    if (prev.length === 0 || prev === visiblePages) return;

    const newIdx = visiblePages.findIndex(
      (p) => p.startIndex === pageIdentityRef.current
    );
    if (newIdx >= 0 && newIdx !== currentPageIndex) {
      setCurrentPageIndex(newIdx);
    }
  }, [visiblePages]); // eslint-disable-line react-hooks/exhaustive-deps

  const furthestProgress = useRef(0);

  const progress = useMemo(() => {
    if (visiblePages.length === 0) return 0;
    const isLastPage = currentPageIndex >= visiblePages.length - 1;
    const raw = isLastPage
      ? 100
      : Math.floor((currentPageIndex / visiblePages.length) * 100);
    furthestProgress.current = Math.max(furthestProgress.current, raw);
    return furthestProgress.current;
  }, [currentPageIndex, visiblePages.length]);

  const setAnswer = useCallback((questionName: string, answer: Answer) => {
    setAnswers((prev) => ({ ...prev, [questionName]: answer }));
  }, []);

  const goToPage = useCallback((index: number) => {
    setCurrentPageIndex(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const nextPage = useCallback(
    (answersOverride?: AnswerMap) => {
      const ans = answersOverride ?? answers;
      const vis = getVisiblePages(pages, questions, ans);
      const cur = visiblePages[currentPageIndex];
      const pos =
        cur != null
          ? vis.findIndex((p) => p.startIndex === cur.startIndex)
          : -1;
      const canAdvance = pos >= 0 && pos < vis.length - 1;

      if (canAdvance) {
        goToPage(pos + 1);
      }
    },
    [currentPageIndex, pages, questions, answers, visiblePages, goToPage]
  );

  const prevPage = useCallback(() => {
    if (currentPageIndex > 0) {
      goToPage(currentPageIndex - 1);
    }
  }, [currentPageIndex, goToPage]);

  const submitQuestionnaire = useCallback(async () => {
    if (!questionnaire) return;

    setIsSubmitting(true);
    try {
      const payload = buildResponsePayload(questionnaire, answers, locale);

      if (isAuthenticated) {
        const idToken = localStorage.getItem("id_token")!;
        await submitAuthenticated(payload, idToken);
      } else {
        const response = await submitUnauthenticated(payload, token);

        if (response.survey_response_jwt_token) {
          const jwt = response.survey_response_jwt_token;
          localStorage.setItem("id_token", jwt);
          if (response.survey_response_jwt_token_exp) {
            localStorage.setItem(
              "id_token_exp",
              String(response.survey_response_jwt_token_exp * 1000)
            );
          }

          try {
            const decoded = jwtDecode<PortalJwtPayload>(jwt);
            if (decoded.app_metadata?.location_id) {
              localStorage.setItem(
                "location_id",
                decoded.app_metadata.location_id
              );
            }
            if (decoded.app_metadata?.ciq_id) {
              localStorage.setItem("ciq_id", decoded.app_metadata.ciq_id);
            }
            if (decoded.email) {
              localStorage.setItem("ciq_email", decoded.email);
            }
          } catch (decodeErr) {
            console.error("Failed to decode portal token:", decodeErr);
          }
        }
      }

      setIsComplete(true);
      storageClear();
      setCurrentPageIndex(-1);
      setAnswers({});
      furthestProgress.current = 0;
      history.push("/complete");
    } catch (err: any) {
      console.error("Submission failed:", err);
      setError("submit_failed");
    } finally {
      setIsSubmitting(false);
    }
  }, [questionnaire, answers, locale, isAuthenticated, token, history]);

  const changeLocale = useCallback(
    (newLocale: string) => {
      setLocale(newLocale);
      setLang(newLocale as Language);

      const url = new URL(window.location.href);
      url.searchParams.set("locale", newLocale);
      window.history.replaceState(window.history.state, "", url.toString());
    },
    [setLang]
  );

  const restartQuestionnaire = useCallback(() => {
    storageClear();
    setAnswers({});
    setCurrentPageIndex(-1);
    setIsComplete(false);
    setError(null);
    furthestProgress.current = 0;
  }, []);

  const contextValue = useMemo(
    () => ({
      questionnaire,
      questions,
      pages,
      visiblePages,
      currentPageIndex,
      answers,
      isLoading,
      error,
      isSubmitting,
      isComplete,
      isAuthenticated,
      progress,
      locale,
      editingFromSummary,
      scrollToQuestion,
      setAnswer,
      goToPage,
      nextPage,
      prevPage,
      submitQuestionnaire,
      changeLocale,
      restartQuestionnaire,
      setEditingFromSummary,
      setScrollToQuestion,
    }),
    [
      questionnaire,
      questions,
      pages,
      visiblePages,
      currentPageIndex,
      answers,
      isLoading,
      error,
      isSubmitting,
      isComplete,
      isAuthenticated,
      progress,
      locale,
      editingFromSummary,
      scrollToQuestion,
      setAnswer,
      goToPage,
      nextPage,
      prevPage,
      submitQuestionnaire,
      changeLocale,
      restartQuestionnaire,
    ]
  );

  return (
    <QuestionnaireContext.Provider value={contextValue}>
      {children}
    </QuestionnaireContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useQuestionnaire = () => {
  const context = useContext(QuestionnaireContext);
  if (context === undefined) {
    throw new Error(
      "useQuestionnaire must be used within a QuestionnaireProvider"
    );
  }
  return context;
};
